import React from 'react';
import { am5, am5themes_Animated, am5xy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createLineOverlay } from './methods/create-line-overlay';
import { createSeries } from './methods/create-series';
import { FONT_FAMILY, FONT_SIZE } from '../../../theme/layout-size';
import { createDownloadButton } from '../shared/create-download-button';
import { getPaddingLeft } from '../shared/helpers';

export type ScatterPlotChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  download: boolean;
  xAxisField: string;
  yAxisField: string;
  xAxisLabel: string;
  yAxisLabel: string;
  numberFormat: string;
  category: string | undefined;
  elementTooltipName: string | undefined;
  elementTooltipText: string | undefined;
  weightField: string | undefined;
  title: string | undefined;
  elementBulletName: string | undefined;
  hideLegend: boolean;
  lineOverlay: 'trendline' | 'x=y' | 'fit' | undefined;
  chartSize: 'medium' | 'large';
  lineSeriesName: string | undefined;
  xAxisFormatter: string | undefined;
  yAxisFormatter: string | undefined;
  xIsDate: boolean;
};

const ScatterPlotChart = ({
  id,
  data,
  fullScreen,
  download,
  category,
  xAxisField,
  yAxisField,
  xAxisLabel,
  yAxisLabel,
  numberFormat,
  title,
  elementTooltipName,
  elementTooltipText,
  weightField,
  hideLegend,
  elementBulletName,
  lineOverlay,
  chartSize,
  lineSeriesName,
  xAxisFormatter,
  yAxisFormatter,
  xIsDate,
}: ScatterPlotChartProps) => {
  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Remove amCharts logo
    root._logo?.dispose();

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelY: 'zoomXY',
        pinchZoomX: true,
        pinchZoomY: true,
        width: am5.p100,
        height: am5.p100,
        layout: root.verticalLayout,
        paddingLeft: getPaddingLeft(download, yAxisLabel),
      })
    );

    if (title) {
      chart.children.unshift(
        am5.Label.new(root, {
          text: title,
          fontSize: 15,
          x: am5.p50,
          centerX: am5.p50,
          paddingTop: 0,
          paddingBottom: 0,
          marginBottom: 20,
        })
      );
    }

    // Set number format
    root.numberFormatter.set('numberFormat', numberFormat);

    // Create axes
    const { xAxis, yAxis } = createAxes({
      root,
      chart,
      xAxisLabel,
      yAxisLabel,
      xAxisFormatter,
      yAxisFormatter,
      xIsDate,
    });

    xAxis.setAll({
      extraMax: 0.1,
      extraMin: 0.1,
    });

    yAxis.setAll({
      extraMax: 0.1,
      extraMin: 0.1,
    });

    // Set data
    createSeries({
      root,
      chart,
      xAxis,
      yAxis,
      category,
      xAxisField,
      yAxisField,
      xAxisLabel,
      yAxisLabel,
      data,
      elementTooltipName,
      elementTooltipText,
      weightField,
      hideLegend,
      elementBulletName,
      chartSize,
      lineSeriesName,
    });

    if (lineOverlay) {
      createLineOverlay({
        root,
        chart,
        xAxis,
        yAxis,
        data,
        xAxisField,
        yAxisField,
        lineOverlay,
      });
    }

    chart.xAxes.each((axis) => {
      axis.get('renderer').labels.template.setAll({
        paddingTop: 10,
        fontSize: FONT_SIZE.xxs,
        fontFamily: FONT_FAMILY,
      });
    });

    chart.yAxes.each((axis) => {
      axis.get('renderer').labels.template.setAll({
        paddingRight: 10,
        fontSize: FONT_SIZE.xxs,
        fontFamily: FONT_FAMILY,
      });
    });

    if (download) createDownloadButton(root, category, data);

    return () => {
      root.dispose();
    };
  }, [data, numberFormat, download, widgetId]);

  return <div id={widgetId} style={{ width: '100%', height: '100%' }} />;
};

export default ScatterPlotChart;
